/**
 * Image Cache Service
 * 
 * Provides in-memory caching and preloading for images to improve performance,
 * especially for allergen icons and product images in the commander.
 * 
 * Features:
 * - In-memory cache to avoid re-fetching images
 * - Preloading of common images (allergens) on app startup
 * - Batch preloading of product images when menus are loaded
 * - Cache status tracking for debugging
 */

class ImageCacheService {
	constructor() {
		// Map of URL -> { loaded: boolean, element: HTMLImageElement, error: boolean }
		this.cache = new Map()
		this.preloadQueue = []
		this.isPreloading = false
		this.maxConcurrentPreloads = 4
		this.activePreloads = 0
	}

	/**
	 * Get a cached image or load it
	 * @param {string} url - The image URL
	 * @returns {Promise<HTMLImageElement>}
	 */
	async getImage(url) {
		if (!url) return null

		const cached = this.cache.get(url)
		if (cached) {
			if (cached.loaded) {
				return cached.element
			}
			if (cached.error) {
				return null
			}
			// Wait for the image to load if it's currently loading
			return cached.promise
		}

		// Start loading the image
		return this.loadImage(url)
	}

	/**
	 * Load an image and cache it
	 * @param {string} url - The image URL
	 * @returns {Promise<HTMLImageElement>}
	 */
	loadImage(url) {
		if (!url) return Promise.resolve(null)

		const existing = this.cache.get(url)
		if (existing?.promise) {
			return existing.promise
		}

		const img = new Image()
		const promise = new Promise((resolve, reject) => {
			img.onload = () => {
				const entry = this.cache.get(url)
				if (entry) {
					entry.loaded = true
					entry.error = false
				}
				resolve(img)
			}
			img.onerror = () => {
				const entry = this.cache.get(url)
				if (entry) {
					entry.loaded = false
					entry.error = true
				}
				reject(new Error(`Failed to load image: ${url}`))
			}
			img.src = url
		})

		this.cache.set(url, {
			loaded: false,
			error: false,
			element: img,
			promise
		})

		return promise
	}

	/**
	 * Check if an image is already cached and loaded
	 * @param {string} url - The image URL
	 * @returns {boolean}
	 */
	isLoaded(url) {
		const cached = this.cache.get(url)
		return cached?.loaded === true
	}

	/**
	 * Preload allergen images from the actual allergen data
	 * Call this with the allergens array from the menu/products data
	 * @param {Array} allergens - Array of allergen objects with pictureName property
	 */
	preloadAllergenImages(allergens = []) {
		if (!allergens || !allergens.length) {
			return
		}
		
		// Get unique allergen picture names
		const uniquePictureNames = [...new Set(
			allergens
				.filter(a => a && a.pictureName)
				.map(a => a.pictureName.toLowerCase())
		)]
		
		const urls = uniquePictureNames.map(name => `assets/img/allergies/${name}.png`)
		this.preloadBatch(urls)
	}
	
	/**
	 * Extract and preload allergen images from menu data
	 * @param {Array} menus - Array of menu objects containing products with allergens
	 */
	preloadAllergensFromMenus(menus) {
		if (!menus) return
		
		const allergens = []
		
		menus.forEach(menu => {
			if (!menu.menuCategories) return
			
			menu.menuCategories.forEach(category => {
				if (!category.products) return
				
				category.products.forEach(product => {
					if (product.allergens && Array.isArray(product.allergens)) {
						allergens.push(...product.allergens)
					}
				})
			})
		})
		
		this.preloadAllergenImages(allergens)
	}

	/**
	 * Preload product images from menu data
	 * @param {Array} menus - Array of menu objects containing products
	 */
	preloadProductImages(menus, urlVenue, resourceId) {
		if (!menus || !urlVenue) return

		const urls = []
		
		menus.forEach(menu => {
			if (!menu.menuCategories) return
			
			menu.menuCategories.forEach(category => {
				if (!category.products) return
				
				category.products.forEach(product => {
					if (product.pictureMenu) {
						// Add both image URL formats
						urls.push(`${urlVenue}/world/image/product/${product.pictureMenu}`)
						if (resourceId) {
							urls.push(`${urlVenue}/world/img/${resourceId}/product/${product.pictureMenu}`)
						}
					}
				})
			})
		})

		// Preload in the background with low priority
		this.preloadBatch(urls, { priority: 'low' })
	}

	/**
	 * Preload a batch of images
	 * @param {Array<string>} urls - Array of image URLs
	 * @param {Object} options - Options for preloading
	 */
	preloadBatch(urls, options = {}) {
		const { priority = 'normal' } = options

		// Filter out already cached URLs
		const newUrls = urls.filter(url => !this.cache.has(url))
		
		if (newUrls.length === 0) return

		// Add to queue based on priority
		if (priority === 'high') {
			this.preloadQueue.unshift(...newUrls)
		} else {
			this.preloadQueue.push(...newUrls)
		}

		this.processPreloadQueue()
	}

	/**
	 * Process the preload queue
	 */
	processPreloadQueue() {
		if (this.isPreloading && this.activePreloads >= this.maxConcurrentPreloads) {
			return
		}

		this.isPreloading = true

		while (this.preloadQueue.length > 0 && this.activePreloads < this.maxConcurrentPreloads) {
			const url = this.preloadQueue.shift()
			this.activePreloads++

			this.loadImage(url)
				.catch(() => {
					// Silently ignore preload errors
				})
				.finally(() => {
					this.activePreloads--
					if (this.preloadQueue.length > 0) {
						// Use requestIdleCallback for low-priority loading
						if (typeof requestIdleCallback !== 'undefined') {
							requestIdleCallback(() => this.processPreloadQueue())
						} else {
							setTimeout(() => this.processPreloadQueue(), 50)
						}
					} else {
						this.isPreloading = false
					}
				})
		}
	}

	/**
	 * Clear the cache (useful for memory management)
	 */
	clear() {
		this.cache.clear()
		this.preloadQueue = []
	}

	/**
	 * Get cache statistics
	 * @returns {Object}
	 */
	getStats() {
		let loaded = 0
		let loading = 0
		let errors = 0

		this.cache.forEach(entry => {
			if (entry.loaded) loaded++
			else if (entry.error) errors++
			else loading++
		})

		return {
			total: this.cache.size,
			loaded,
			loading,
			errors,
			queueSize: this.preloadQueue.length
		}
	}
}

// Export singleton instance
export const imageCacheService = new ImageCacheService()

// Also export the class for testing
export { ImageCacheService }
